import React, { useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { captureMessage } from '@tink/core/src/sentry';
import { resetTransfer as resetTransferConnect } from '../../../../../stores/transfer-store';
import { Transfer } from '../../../../DirectPayments/models/transfer';
import { AppState } from '../../../../../stores/redux-store';
import { LoadingOverlay } from '../../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { SuccessNotification } from '../../../../../core/screens/SuccessNotification/SuccessNotification';
import { messages } from '../../../../components/InsufficientFundsOverlay/messages';
import { commonMessages } from '../../../../../components/atoms/CommonMessages/messages';
import { messages as paymentMessages } from '../../../../DirectPayments/components/PaymentFailureOverlay/messages';
import { messages as commonMessagesPayment } from '../../../../../core/screens/SuccessNotification/molecules/SuccessOverlay/messages';
import { ApplicationSuccessResult } from '../../../../../models/ApplicationSuccessResult';
import { PaymentFailureOverlay } from '../../../../DirectPayments/components/PaymentFailureOverlay/PaymentFailureOverlay';
import { PaymentCancelledOverlay } from '../../../../DirectPayments/components/PaymentCancelledOverlay/PaymentCancelledOverlay';
import { ERR_USER_CANCELLED } from '../../../../../utilities/error';
import { setApplicationError as setApplicationErrorConnect } from '../../../../../stores/app-state-store';
import { ExtendBankId } from '../molecules/containers/ExtendBankId/ExtendBankId';
import { useApplicationSuccessResultPayloadPay } from '../../../hooks/useApplicationSuccessResultPayloadPay/useApplicationSuccessResultPayloadPay';
import { Caption } from '../../../../../components/atoms/Text/Text';
import { useTransferPoller } from './useTransferPoller';

export type TransferMonitorProps = WrappedComponentProps & PropsFromRedux;

export const TransferMonitorPresenter: React.FC<TransferMonitorProps> = ({
  intl,
  session,
  transfer,
  paymentDetails,
  setApplicationError,
  resetTransfer,
}: TransferMonitorProps) => {
  useTransferPoller();

  const terminateFlow = useCallback(() => {
    setApplicationError({
      ...ERR_USER_CANCELLED,
    });
  }, [setApplicationError]);

  const retryTransfer = () => {
    resetTransfer();
  };

  const { successResult, pendingAC } = useApplicationSuccessResultPayloadPay();

  if (Transfer.hasInternalStatusNeedsExtendedBankId(transfer)) {
    return <ExtendBankId />;
  }

  if (Transfer.hasInternalStatusDuplicatePayment(transfer)) {
    return (
      <PaymentCancelledOverlay
        paymentId={paymentDetails!.id}
        text={intl.formatMessage(messages.duplicatePayment)}
        hint={transfer?.statusMessage}
        primaryButtonAction={terminateFlow}
        primaryButtonText={intl.formatMessage(commonMessages.ok)}
      />
    );
  }

  if (Transfer.isCancelled(transfer)) {
    return session?.sourceAccountNumber ? (
      <PaymentCancelledOverlay
        paymentId={paymentDetails!.id}
        text={transfer?.statusMessage}
        primaryButtonAction={terminateFlow}
      />
    ) : (
      <PaymentCancelledOverlay
        paymentId={paymentDetails!.id}
        text={transfer?.statusMessage}
        primaryButtonAction={retryTransfer}
        primaryButtonText={intl.formatMessage(messages.backToAccountSelection)}
        secondaryButtonAction={terminateFlow}
      />
    );
  }

  if (Transfer.isSent(transfer) && !pendingAC) {
    return (
      <SuccessNotification
        overlayHeading={<FormattedMessage {...messages.successText} />}
        applicationSuccessResult={ApplicationSuccessResult.pay(successResult)}
        overlaySubtext={
          <Caption color="secondary">
            <FormattedMessage
              {...commonMessagesPayment.paymentId}
              values={{ paymentId: paymentDetails?.id }}
            />
          </Caption>
        }
      />
    );
  }

  if (Transfer.isFailed(transfer)) {
    return session?.sourceAccountNumber ? (
      <PaymentFailureOverlay
        paymentId={paymentDetails!.id}
        text={transfer?.statusMessage}
        hint={intl.formatMessage(messages.failedTextSourceAccount)}
        primaryButtonAction={terminateFlow}
        primaryButtonText={intl.formatMessage(paymentMessages.terminateFlow)}
      />
    ) : (
      <PaymentFailureOverlay
        paymentId={paymentDetails!.id}
        text={transfer?.statusMessage}
        primaryButtonAction={retryTransfer}
        primaryButtonText={intl.formatMessage(messages.backToAccountSelection)}
        secondaryButtonAction={terminateFlow}
      />
    );
  }

  if (
    Transfer.isAwaiting(transfer) ||
    Transfer.isCreated(transfer) ||
    (Transfer.isSent(transfer) && pendingAC)
  ) {
    return <LoadingOverlay />;
  }

  captureMessage('We are not handling transfer status', {
    custom: {
      transferStatus: transfer?.status || 'N/A',
    },
  });

  return <LoadingOverlay />;
};

const mapStateToProps = (state: AppState) => {
  return {
    transfer: state.transfer.data,
    session: state.session.data,
    paymentDetails: state.paymentDetails.data,
  };
};

const mapDispatchToProps = {
  setApplicationError: setApplicationErrorConnect,
  resetTransfer: resetTransferConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const TransferMonitor = injectIntl(connector(TransferMonitorPresenter));
